import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import DOMPurify from 'dompurify'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Sanitizes HTML content to prevent XSS attacks.
 * Allows common formatting tags like p, br, strong, em, ul, ol, li, etc.
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'b', 'i', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Sanitizes a URL for safe tracking by removing sensitive parameters and limiting length.
 * Used for origin URL tracking when navigating from home widget to chat widget.
 */
export function sanitizeOriginUrl(url: string): string | null {
  try {
    const u = new URL(url);
    
    // Remove sensitive query parameters
    const sensitiveParams = [
      'token', 'auth', 'code', 'session', 'key', 
      'password', 'secret', 'email', 'api_key',
      'access_token', 'refresh_token'
    ];
    sensitiveParams.forEach(param => u.searchParams.delete(param));
    
    // Remove fragment (hash)
    u.hash = '';
    
    // Truncate to reasonable length
    const sanitized = u.toString();
    return sanitized.length > 512 ? sanitized.slice(0, 512) : sanitized;
  } catch {
    // Invalid URL
    return null;
  }
}

import type { RecommendedProduct } from "./types";
import type { Product } from "./mockData";

type VariantCombo = { values: string[]; sku?: string; url?: string; price?: number; compare_at_price?: number; image_ids?: string[] };

function toDisplayAmount(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

/**
 * Resolve backend-provided picked variant values into a full ordered selection.
 *
 * - If backend sends a full ordered selection (length === dims.length) and it exactly matches a combo, keep it.
 * - Otherwise, treat picked values as constraints and find the first combo that contains all picked values.
 * - Returns null when picks are empty/invalid or no matching combo exists.
 */
export function resolvePickedVariantSelection(
  dims: string[],
  combos: VariantCombo[],
  pickedVariantValues?: string[]
): string[] | null {
  if (!Array.isArray(dims) || dims.length === 0) return null;
  if (!Array.isArray(combos) || combos.length === 0) return null;

  const picked = Array.isArray(pickedVariantValues)
    ? pickedVariantValues
        .filter((v): v is string => typeof v === "string")
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
    : [];

  if (picked.length === 0) return null;

  // Prefer exact ordered match when a full selection is provided.
  if (picked.length === dims.length) {
    const exact = combos.find((c) => c.values.every((v, i) => v === picked[i]));
    if (exact) return picked.slice();
  }

  // Allow partial/out-of-order picks: first combo that contains all picked values.
  const uniqPicked = Array.from(new Set(picked));
  const match = combos.find((c) => uniqPicked.every((pv) => c.values.includes(pv)));
  return match ? match.values.slice() : null;
}

export function mapRecommendedToProduct(recommendedProduct: RecommendedProduct, searchId?: string): Product {
  const dims = Array.isArray(recommendedProduct.variant_dimensions) ? recommendedProduct.variant_dimensions : [];
  const combosRaw = Array.isArray(recommendedProduct.variant_combinations) ? recommendedProduct.variant_combinations : [];
  const combos: VariantCombo[] = combosRaw
    .filter((c) => Array.isArray(c?.values) && c.values.length === dims.length)
    .map((c) => {
      const imageIdsRaw = (c as any)?.image_ids;
      const image_ids = Array.isArray(imageIdsRaw)
        ? imageIdsRaw.filter((id: unknown): id is string => typeof id === "string" && id.length > 0)
        : undefined;
      return {
        values: (c as any).values,
        sku: (c as any).sku,
        url: typeof (c as any).url === "string" ? (c as any).url : undefined,
        price: (c as any).price,
        compare_at_price: (c as any).compare_at_price,
        image_ids: image_ids && image_ids.length > 0 ? image_ids : undefined,
      };
    });
  const picked = Array.isArray(recommendedProduct.picked_variant_values) ? recommendedProduct.picked_variant_values : [];

  const findExact = (values: string[]) => combos.find((c) => c.values.every((v, i) => v === values[i]));
  const getFirstOptionsForIndex = (index: number, prefix: string[]): string | undefined => {
    const set = new Set<string>();
    for (const c of combos) {
      let ok = true;
      for (let i = 0; i < prefix.length; i++) {
        if (c.values[i] !== prefix[i]) { ok = false; break; }
      }
      if (!ok) continue;
      const val = c.values[index];
      if (!set.has(val)) {
        set.add(val);
      }
    }
    return [...set][0];
  };
  const deriveCascade = (): string[] => {
    const selection: string[] = [];
    for (let i = 0; i < dims.length; i++) {
      const next = getFirstOptionsForIndex(i, selection);
      if (typeof next === 'string') selection[i] = next; else selection[i] = '';
    }
    return selection;
  };

  const resolvedPicked = resolvePickedVariantSelection(dims, combos, picked);
  const initialSelected: string[] = resolvedPicked ?? deriveCascade();
  const initialCombo = findExact(initialSelected) ?? undefined;
  const initialPriceStr = toDisplayAmount(initialCombo?.price ?? (recommendedProduct as any).price);
  const initialCompareAtStr = toDisplayAmount(initialCombo?.compare_at_price ?? (recommendedProduct as any).compare_at_price);

  const imageEntries = Array.isArray(recommendedProduct.images)
    ? recommendedProduct.images
    : typeof (recommendedProduct as any).image === "string"
      ? [(recommendedProduct as any).image]
      : [];

  const imageMeta = imageEntries
    .map((entry) => {
      if (!entry || typeof entry !== "object") return undefined;
      const url = (entry as any).url;
      const id = (entry as any).id;
      if (typeof url !== "string" || url.length === 0) return undefined;
      if (typeof id !== "string" || id.length === 0) return undefined;
      return { id, url };
    })
    .filter((v): v is { id: string; url: string } => Boolean(v));

  const normalizedImages = imageEntries
    .map((entry) => {
      if (!entry) return undefined;
      if (typeof entry === "string") return entry;
      if (typeof entry === "object" && typeof (entry as any).url === "string") {
        return (entry as any).url;
      }
      return undefined;
    })
    .filter((url): url is string => Boolean(url));
  const resolvedImages = normalizedImages;

  return {
    id: recommendedProduct.id.toString(),
    title: recommendedProduct.name,
    price: initialPriceStr || "",
    compareAtPrice: initialCompareAtStr || undefined,
    currencyDisplay: recommendedProduct.currency_display ?? undefined,
    description: (recommendedProduct.description ?? "") || "",
    reason: recommendedProduct.reason ?? undefined,
    images: resolvedImages,
    imageMeta: imageMeta.length > 0 ? imageMeta : undefined,
    review: {
      name: "John",
      text: "Mycket bra kvalitet, passar perfekt!"
    },
    searchId,
    productHandle: recommendedProduct.product_handle ?? undefined,
    productUrl: recommendedProduct.url ?? recommendedProduct.product_url ?? undefined,
    rawImageObjects: imageEntries,
    variantDimensions: dims.length > 0 ? dims : undefined,
    variantCombinations: combos.length > 0 ? combos : undefined,
    pickedVariantValues: picked.length > 0 ? picked : undefined,
  } as Product;
}

